import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  gameApi,
  StatePerformanceAggregate,
  StatePerformanceSnapshot,
  StatePerformanceStepMetric,
  UserRole,
} from '../api';

interface AdminStatePerformanceViewProps {
  authRole: UserRole | null;
  isAuthenticated: boolean;
  onBack: () => void;
}

const formatMilliseconds = (value?: number | null): string => {
  if (value == null) return '—';
  if (value > 0 && value < 0.01) return '<0.01 ms';
  if (value >= 1000) return `${(value / 1000).toFixed(2)} s`;
  return `${value.toFixed(value >= 10 ? 1 : 2)} ms`;
};

const formatDate = (value: string): string => new Date(value).toLocaleString();

const findAggregate = (
  snapshot: StatePerformanceSnapshot,
  classification: string
): StatePerformanceAggregate | undefined =>
  snapshot.aggregates.find(aggregate => aggregate.classification === classification);

const findStep = (
  aggregate: StatePerformanceAggregate | undefined,
  collection: 'stages' | 'details',
  name: string
): StatePerformanceStepMetric | undefined =>
  aggregate?.[collection].find(step => step.name === name);

interface TimingTableProps {
  title: string;
  collection: 'stages' | 'details';
  all?: StatePerformanceAggregate;
  cold?: StatePerformanceAggregate;
  warm?: StatePerformanceAggregate;
}

const TimingTable: React.FC<TimingTableProps> = ({ title, collection, all, cold, warm }) => {
  const rows = all?.[collection] ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="state-performance-section">
      <h3>{title}</h3>
      <div className="state-performance-table-wrap">
        <table className="state-performance-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Calls</th>
              <th>All avg</th>
              <th>All max</th>
              <th>Cold avg</th>
              <th>Cold max</th>
              <th>Warm avg</th>
              <th>Warm max</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(step => {
              const coldStep = findStep(cold, collection, step.name);
              const warmStep = findStep(warm, collection, step.name);
              return (
                <tr key={step.name}>
                  <td>{step.name}</td>
                  <td>{step.count}</td>
                  <td>{formatMilliseconds(step.averageMilliseconds)}</td>
                  <td>{formatMilliseconds(step.maximumMilliseconds)}</td>
                  <td>{formatMilliseconds(coldStep?.averageMilliseconds)}</td>
                  <td>{formatMilliseconds(coldStep?.maximumMilliseconds)}</td>
                  <td>{formatMilliseconds(warmStep?.averageMilliseconds)}</td>
                  <td>{formatMilliseconds(warmStep?.maximumMilliseconds)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const AdminStatePerformanceView: React.FC<AdminStatePerformanceViewProps> = ({
  authRole,
  isAuthenticated,
  onBack,
}) => {
  const [snapshot, setSnapshot] = useState<StatePerformanceSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatistics = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Login required');
      return;
    }
    if (authRole !== 'Admin') {
      setError('Admin access required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setSnapshot(await gameApi.getStatePerformanceStatistics());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load state performance statistics');
    } finally {
      setLoading(false);
    }
  }, [authRole, isAuthenticated]);

  useEffect(() => {
    void loadStatistics();
  }, [loadStatistics]);

  const aggregates = useMemo(() => {
    if (!snapshot) return {};
    return {
      all: findAggregate(snapshot, 'All'),
      cold: findAggregate(snapshot, 'Cold'),
      warm: findAggregate(snapshot, 'Warm'),
      finished: findAggregate(snapshot, 'FinishedSnapshot'),
    };
  }, [snapshot]);

  return (
    <main className="state-performance-view">
      <div className="state-performance-header">
        <div>
          <h2>State Performance</h2>
          <p>In-memory timings for GET /api/v2/game/state during this server lifetime.</p>
        </div>
        <div className="state-performance-actions">
          <button type="button" onClick={onBack}>Back</button>
          <button type="button" onClick={() => void loadStatistics()} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {!snapshot && loading && <div className="loading">Loading performance statistics…</div>}

      {snapshot && (
        <>
          <div className="state-performance-meta">
            <span>Server started: {formatDate(snapshot.serverStartedAtUtc)}</span>
            <span>Snapshot: {formatDate(snapshot.generatedAtUtc)}</span>
            <span>Recent limit: {snapshot.recentRequestLimit}</span>
          </div>

          <section className="state-performance-cards" aria-label="State request summary">
            {[
              ['All requests', snapshot.summary.requestCount, aggregates.all],
              ['Cold', snapshot.summary.coldRequestCount, aggregates.cold],
              ['Warm', snapshot.summary.warmRequestCount, aggregates.warm],
              ['Finished shortcut', snapshot.summary.finishedSnapshotRequestCount, aggregates.finished],
            ].map(([label, count, aggregate]) => {
              const typedAggregate = aggregate as StatePerformanceAggregate | undefined;
              return (
                <article className="state-performance-card" key={label as string}>
                  <h3>{label as string}</h3>
                  <strong>{count as number}</strong>
                  <span>Avg {formatMilliseconds(typedAggregate?.totalDuration.averageMilliseconds)}</span>
                  <span>P95 {formatMilliseconds(typedAggregate?.totalDuration.p95Milliseconds)}</span>
                  <span>Max {formatMilliseconds(typedAggregate?.totalDuration.maximumMilliseconds)}</span>
                </article>
              );
            })}
          </section>

          <div className="state-performance-outcomes">
            Success: {snapshot.summary.successCount} · Not found: {snapshot.summary.notFoundCount} · Rejected: {snapshot.summary.rejectedCount} · Failed: {snapshot.summary.failedCount} · Cancelled: {snapshot.summary.cancelledCount}
          </div>

          {snapshot.summary.requestCount === 0 ? (
            <div className="state-performance-empty">No state requests have been recorded yet.</div>
          ) : (
            <>
              <TimingTable
                title="Request stages"
                collection="stages"
                all={aggregates.all}
                cold={aggregates.cold}
                warm={aggregates.warm}
              />
              <TimingTable
                title="Replay and payload details"
                collection="details"
                all={aggregates.all}
                cold={aggregates.cold}
                warm={aggregates.warm}
              />
            </>
          )}

          <section className="state-performance-section">
            <h3>Shared execution</h3>
            <p>Each factory is counted once, including action responses and failed work. Request waits include shared execution; parent and child timings overlap.</p>
            {(snapshot.workAggregates ?? []).map(work => (
              <div key={work.classification}>
                <h4>{work.classification}: {work.requestCount} operations · Avg {formatMilliseconds(work.totalDuration.averageMilliseconds)} · Max {formatMilliseconds(work.totalDuration.maximumMilliseconds)}</h4>
                <TimingTable title={work.classification + ' details'} collection="details" all={work} />
              </div>
            ))}
            <h3>Slow or failed shared work</h3>
            {(snapshot.recentSlowWork ?? []).map(work => (
              <details key={work.workId}>
                <summary>{work.kind} · {work.outcome} · {formatMilliseconds(work.totalDurationMilliseconds)}</summary>
                <div>Work: {work.workId} · Game: {work.gameId} · {formatDate(work.startedAtUtc)}</div>
                {work.details.map((step, index) => <div key={index}>{step.name}: {formatMilliseconds(step.durationMilliseconds)}</div>)}
                {Object.entries(work.dimensions).map(([name, value]) => <div key={name}>{name}: {value}</div>)}
              </details>
            ))}
          </section>
          <section className="state-performance-section">
            <h3>Recent requests</h3>
            {snapshot.recentRequests.length === 0 ? (
              <div className="state-performance-empty">No recent requests.</div>
            ) : (
              <div className="state-performance-recent">
                {snapshot.recentRequests.map((request, index) => (
                  <details key={`${request.startedAtUtc}-${index}`}>
                    <summary>
                      <span>{formatDate(request.startedAtUtc)}</span>
                      <span className={`state-performance-badge ${request.classification.toLowerCase()}`}>
                        {request.classification}
                      </span>
                      <span>{request.outcome}{request.statusCode ? ` (${request.statusCode})` : ''}</span>
                      <strong>{formatMilliseconds(request.totalDurationMilliseconds)}</strong>
                      <span>{request.slowestStage ?? 'No stages'}: {formatMilliseconds(request.slowestStageMilliseconds)}</span>
                    </summary>
                    <div className="state-performance-request-detail">
                      <div>Game: {request.gameId ?? 'Not resolved'}</div>
                      <div>Concurrent requests: {request.concurrentRequests ?? 1}</div>
                      {Object.entries(request.dimensions ?? {}).map(([name, value]) => (
                        <div key={`dimension-${name}`}>
                          <span>{name}</span>
                          <span>{value}</span>
                        </div>
                      ))}
                      {[...request.stages, ...request.details].map((step, stepIndex) => (
                        <div key={`${step.name}-${stepIndex}`}>
                          <span>{step.name}</span>
                          <span>{formatMilliseconds(step.durationMilliseconds)}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
};
